import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);

    window.addEventListener('resize', this.#resizeListener);

    this.overflowMenu?.addEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    if (this.dataset.menuTrigger === 'click') {
      document.addEventListener('click', this.#onDocumentClick);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    window.removeEventListener('resize', this.#resizeListener);

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    if (this.#pointerFrame) {
      cancelAnimationFrame(this.#pointerFrame);
    }

    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }

    this.overflowMenu?.removeEventListener(
      'pointerleave',
      this.#overflowSubmenuListener
    );

    document.removeEventListener(
      'click',
      this.#onDocumentClick
    );
  }

  #resizeListener = debounce(() => {
    setHeaderMenuStyle();
  }, 100);

  #overflowSubmenuListener = () => {
    this.#deactivate();
  };

  #state = {
    activeItem: null,
    activeSubmenu: null,
  };

  #pointerIdleTimer;

  #pointerFrame = null;

  #lastPointer = { x: 0, y: 0 };

  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;

    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving =
      Math.abs(event.movementX) >= 1 ||
      Math.abs(event.movementY) >= 1;

    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);

    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (!this.#state.activeItem) return;

        this.#state.activeItem.dataset.safetyBox = 'false';

        if (this.#pointerFrame) return;

        this.#pointerFrame = requestAnimationFrame(() => {
          this.#pointerFrame = null;
          this.#reconcilePointerTarget();
        });
      }, 250);
    }
  };

  #reconcilePointerTarget = () => {
    const { x, y } = this.#lastPointer;

    const target = document.elementFromPoint(x, y);

    if (!(target instanceof Element)) return;

    const listItem = target.closest('.menu-list__list-item');

    if (
      listItem &&
      !listItem.contains(this.#state.activeItem)
    ) {
      listItem.dispatchEvent(
        new PointerEvent('pointerenter', {
          bubbles: false,
        })
      );
    }
  };

  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener(
        'pointermove',
        this.#onPointerMove,
        { passive: true }
      );
    }

    const rect = item.getBoundingClientRect();

    const isOverlap =
      this.headerComponent?.hasAttribute(
        'data-submenu-overlap-bottom-row'
      );

    const boundary = isOverlap
      ? this.headerComponent?.querySelector('.header__row--top')
      : this.headerComponent;

    const boundaryRect = boundary?.getBoundingClientRect();

    item.style.setProperty(
      '--box-height',
      `${(boundaryRect?.bottom ?? 0) - rect.top}px`
    );
  }

  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);

    this.#pointerIdleTimer = undefined;

    item.style.removeProperty('--box-height');

    delete item.dataset.safetyBox;
  }

  get overflowMenu() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector(
      '[part="overflow"]'
    );
  }

  get overflowListHovered() {
    return (
      this.refs.overflowMenu?.shadowRoot
        ?.querySelector('[part="overflow-list"]')
        ?.matches(':hover') ?? false
    );
  }

  get headerComponent() {
    return this.closest('header-component');
  }

  #onDocumentClick = (event) => {
    if (!this.#state.activeItem) return;

    if (!(event.target instanceof Node)) return;

    if (this.contains(event.target)) return;

    const activeSubmenu = this.#state.activeSubmenu;

    if (
      activeSubmenu &&
      activeSubmenu.contains(event.target)
    ) {
      return;
    }

    this.#deactivate();
  };

  toggle = (event) => {
    if (!(event.target instanceof Element)) return;

    const path = event.composedPath();

    let clickedLink = null;
    let clickedItem = null;
    let isNested = false;

    for (let i = 0; i < path.length; i++) {
      const el = path[i];

      if (!(el instanceof Element)) continue;

      if (!clickedLink && el.matches('.menu-list__link')) {
        clickedLink = el;
      }

      if (clickedLink && !clickedItem && el.matches('li')) {
        clickedItem = el;
      }

      if (clickedLink && el.matches('.menu-list__submenu')) {
        isNested = true;
      }

      if (el.matches('header-menu')) break;
    }

    if (!clickedLink || !clickedItem) return;

    const hasSubmenu =
      clickedLink.getAttribute('aria-haspopup') === 'true';

    if (isNested && hasSubmenu) {
      event.preventDefault();

      const submenu = clickedItem.querySelector(
        ':scope > div > [ref="submenu[]"], :scope > [ref="submenu[]"]'
      );

      if (!submenu) return;

      const isExpanded =
        clickedLink.getAttribute('aria-expanded') === 'true';

      const siblingSubmenus =
        clickedItem.parentElement?.children ?? [];

      for (const sibling of siblingSubmenus) {
        if (!(sibling instanceof HTMLElement)) continue;

        const siblingSubmenu = sibling.querySelector(
          ':scope > div > [ref="submenu[]"], :scope > [ref="submenu[]"]'
        );

        const siblingLink = sibling.querySelector(
          ':scope > div > .menu-list__link, :scope > .menu-list__link'
        );

        if (
          siblingSubmenu &&
          siblingSubmenu !== submenu
        ) {
          siblingSubmenu.style.visibility = 'hidden';
          siblingSubmenu.style.opacity = '0';

          siblingLink?.setAttribute(
            'aria-expanded',
            'false'
          );
        }
      }

      clickedLink.setAttribute(
        'aria-expanded',
        (!isExpanded).toString()
      );

      submenu.style.visibility = isExpanded
        ? 'hidden'
        : 'visible';

      submenu.style.opacity = isExpanded
        ? '0'
        : '1';

      return;
    }

    const topLevelItem = path.find(
      (el) =>
        el instanceof Element &&
        el.matches('.menu-list__list-item')
    );

    const topLevelLink =
      topLevelItem?.querySelector('.menu-list__link');

    if (!topLevelLink) return;

    if (hasSubmenu) {
      event.preventDefault();
    }

    if (topLevelLink === this.#state.activeItem) {
      this.#deactivate();
      return;
    }

    if (
      this.#state.activeItem &&
      !topLevelItem.contains(this.#state.activeItem)
    ) {
      this.#deactivate();
    }

    this.activate(event);
  };

  activate = debounce((event) => {
    this.dispatchEvent(new MegaMenuHoverEvent());

    if (
      !(event.target instanceof Element) ||
      !this.headerComponent
    ) {
      return;
    }

    const item = findMenuItemWrapper(event.target);

    if (!item) return;

    if (item.closest('.menu-list__submenu')) {
      return;
    }

    const link = item.querySelector('.menu-list__link');

    if (!link || link === this.#state.activeItem) {
      return;
    }

    const submenu = findSubmenu(item);

    this.#state.activeItem = link;
    this.#state.activeSubmenu = submenu;

    this.ariaExpanded = 'true';
    link.ariaExpanded = 'true';

    if (submenu) {
      submenu.dataset.active = '';

      requestAnimationFrame(() => {
        const height = submenu.scrollHeight;

        this.headerComponent?.style.setProperty(
          '--submenu-height',
          `${height}px`
        );
      });
    } else {
      this.headerComponent?.style.setProperty(
        '--submenu-height',
        '0px'
      );
    }

    this.style.setProperty('--submenu-opacity', '1');

    this.#startPointerTracking(
      link,
      this.#state.activeItem
    );
  }, 100);

  deactivate = (event) => {
    const relatedTarget =
      event.relatedTarget instanceof Node
        ? event.relatedTarget
        : null;

    const itemToDeactivate = this.#state.activeItem;

    if (!itemToDeactivate) return;

    const activeSubmenu = this.#state.activeSubmenu;

    if (
      relatedTarget &&
      activeSubmenu?.contains(relatedTarget)
    ) {
      return;
    }

    const originalMenuItemWrapper =
      itemToDeactivate.closest(
        '.menu-list__list-item'
      );

    if (
      originalMenuItemWrapper?.contains(
        relatedTarget
      )
    ) {
      return;
    }

    this.#deactivateDebounced(
      event,
      itemToDeactivate
    );
  };

  #deactivateDebounced = debounce(
    (event, itemToDeactivate) => {
      if (!(event.target instanceof Element)) return;

      this.#deactivate(itemToDeactivate);
    },
    300
  );

  #deactivateSubmenu = (submenuToDeactivate) => {
    if (!submenuToDeactivate) return;

    submenuToDeactivate.setAttribute(
      'aria-expanded',
      'false'
    );

    delete submenuToDeactivate.dataset.active;

    const nestedSubmenus =
      submenuToDeactivate.querySelectorAll(
        ':scope .menu-list__submenu'
      );

    for (const nestedSubmenu of nestedSubmenus) {
      nestedSubmenu.setAttribute(
        'aria-expanded',
        'false'
      );

      delete nestedSubmenu.dataset.active;

      nestedSubmenu.style.visibility = '';
      nestedSubmenu.style.opacity = '';
    }

    const expandedLinks =
      submenuToDeactivate.querySelectorAll(
        '.menu-list__link[aria-expanded="true"]'
      );

    for (const link of expandedLinks) {
      link.setAttribute('aria-expanded', 'false');
    }
  };

  #deactivate = (item = this.#state.activeItem) => {
    if (!item || item !== this.#state.activeItem) {
      return;
    }

    if (
      this.overflowListHovered ||
      this.overflowMenu?.matches(':hover')
    ) {
      return;
    }

    this.headerComponent?.style.setProperty(
      '--submenu-height',
      '0px'
    );

    this.style.setProperty('--submenu-opacity', '0');

    this.dataset.overflowExpanded = 'false';

    const submenu = findSubmenu(
      item.closest('.menu-list__list-item')
    );

    document.body.removeEventListener(
      'pointermove',
      this.#onPointerMove
    );

    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.#state.activeSubmenu = null;

    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    if (submenu) {
      this.#deactivateSubmenu(submenu);
    }
  };

  #preloadImages = () => {
    const images = this.querySelectorAll(
      'img[loading="lazy"]'
    );

    images.forEach((image) => {
      image.removeAttribute('loading');
    });
  };
}

if (!customElements.get('header-menu')) {
  customElements.define(
    'header-menu',
    HeaderMenu
  );
}

function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    return findMenuItem(
      element.parentElement?.querySelector(
        '[slot="overflow"]'
      )
    );
  }

  return element.matches('.menu-list__link')
    ? element
    : element.querySelector('.menu-list__link');
}

function findMenuItemWrapper(element) {
  if (!(element instanceof Element)) return null;

  return element.closest('.menu-list__list-item');
}

function findSubmenu(element) {
  const listItem =
    element?.closest('.menu-list__list-item');

  if (!listItem) return null;

  const submenu = listItem.querySelector(
    ':scope > div > [ref="submenu[]"], :scope > [ref="submenu[]"]'
  );

  return submenu instanceof HTMLElement
    ? submenu
    : null;
}